import {
    readPanelSessionStateMap,
    writePanelSessionStateMap,
} from '../../shared/panels/panelSessionState.ts';
import type { PanelAttachment, PanelSessionState, PanelStateStorage } from '../../shared/panels/panelSessionState.ts';

export const FACILITY_SLIDEOUT_PANEL_IDS = {
    assetTags: 'asset-tags',
    knowledgeBook: 'knowledge-book',
    knowledgeViewEdit: 'knowledge-view-edit',
    facilityAssetSets: 'facility-asset-sets',
    assetSetVisualization: 'asset-set-visualization',
    measurements: 'measurements',
    assemblyLibrary: 'assembly-library',
} as const;

export type FacilitySlideoutPanelId = typeof FACILITY_SLIDEOUT_PANEL_IDS[keyof typeof FACILITY_SLIDEOUT_PANEL_IDS];

export const ACTIVE_FACILITY_SLIDEOUT_PANEL_KEY = '__facilityActiveSlideoutPanelId';
const LAST_KNOWLEDGE_VIEWS_PANEL_KEY = '__facilityLastKnowledgeViewsPanelId';

type KnowledgeViewsPanelId =
    | typeof FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook
    | typeof FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit;

export const canOpenKnowledgeViewEditPanel = (
    hasModifyKnowledgeBookPermission: boolean,
    selectedKnowledgeBookID?: string | null,
    selectedKnowledgeViewID?: string | null
): boolean => {
    return hasModifyKnowledgeBookPermission &&
        !!selectedKnowledgeBookID &&
        !!selectedKnowledgeViewID;
};

const FACILITY_SLIDEOUT_PANEL_ID_LIST = Object.values(FACILITY_SLIDEOUT_PANEL_IDS) as FacilitySlideoutPanelId[];
const FLOATING_CAPABLE_FACILITY_SLIDEOUT_PANEL_IDS: FacilitySlideoutPanelId[] = [
    FACILITY_SLIDEOUT_PANEL_IDS.assetTags,
    FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook,
    FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit,
    FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets,
    FACILITY_SLIDEOUT_PANEL_IDS.assetSetVisualization,
    FACILITY_SLIDEOUT_PANEL_IDS.measurements,
    FACILITY_SLIDEOUT_PANEL_IDS.assemblyLibrary,
];

const getMutuallyExclusivePanelId = (
    panelId: FacilitySlideoutPanelId
): FacilitySlideoutPanelId | null => {
    if (panelId === FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook) {
        return FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets;
    }
    if (panelId === FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets) {
        return FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook;
    }
    return null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null;
};

const isFacilitySlideoutPanelId = (value: unknown): value is FacilitySlideoutPanelId => {
    return typeof value === 'string' && FACILITY_SLIDEOUT_PANEL_ID_LIST.includes(value as FacilitySlideoutPanelId);
};

const isKnowledgeViewsPanelId = (value: unknown): value is KnowledgeViewsPanelId => {
    return value === FACILITY_SLIDEOUT_PANEL_IDS.knowledgeBook ||
        value === FACILITY_SLIDEOUT_PANEL_IDS.knowledgeViewEdit;
};

export const getLastKnowledgeViewsPanelId = (
    storage?: PanelStateStorage | null
): KnowledgeViewsPanelId | null => {
    const stateMap = readPanelSessionStateMap(storage);
    const panelId = stateMap[LAST_KNOWLEDGE_VIEWS_PANEL_KEY];
    return isKnowledgeViewsPanelId(panelId) ? panelId : null;
};

const isFloatingCapableFacilitySlideoutPanelId = (panelId: FacilitySlideoutPanelId): boolean => {
    return FLOATING_CAPABLE_FACILITY_SLIDEOUT_PANEL_IDS.includes(panelId);
};

const getPanelAttachmentFromStateMap = (
    stateMap: Record<string, unknown>,
    panelId: FacilitySlideoutPanelId,
    attachmentOverride?: PanelAttachment
): PanelAttachment => {
    if (attachmentOverride && isFloatingCapableFacilitySlideoutPanelId(panelId)) {
        return attachmentOverride;
    }

    const entry = stateMap[panelId];
    if (isFloatingCapableFacilitySlideoutPanelId(panelId) && isRecord(entry) && entry.attachment === 'floating') {
        return 'floating';
    }

    return 'pinned';
};

export const isFacilitySlideoutPanelFloating = (
    panelId: FacilitySlideoutPanelId,
    storage?: PanelStateStorage | null
): boolean => {
    const stateMap = readPanelSessionStateMap(storage);
    return getPanelAttachmentFromStateMap(stateMap, panelId) === 'floating';
};

export const activateFacilitySlideoutPanelSession = (
    panelId: FacilitySlideoutPanelId,
    storage?: PanelStateStorage | null,
    attachmentOverride?: PanelAttachment
): void => {
    const stateMap = readPanelSessionStateMap(storage);
    const targetAttachment = getPanelAttachmentFromStateMap(stateMap, panelId, attachmentOverride);
    const mutuallyExclusivePanelId = getMutuallyExclusivePanelId(panelId);

    stateMap[ACTIVE_FACILITY_SLIDEOUT_PANEL_KEY] = panelId;
    if (isKnowledgeViewsPanelId(panelId)) {
        stateMap[LAST_KNOWLEDGE_VIEWS_PANEL_KEY] = panelId;
    }
    FACILITY_SLIDEOUT_PANEL_ID_LIST.forEach(id => {
        const entry = stateMap[id];
        const isTargetPanel = id === panelId;
        const panelAttachment = getPanelAttachmentFromStateMap(stateMap, id, isTargetPanel ? targetAttachment : undefined);
        const shouldClosePanel = !isTargetPanel && (
            id === mutuallyExclusivePanelId ||
            (targetAttachment === 'pinned' && panelAttachment === 'pinned')
        );

        if (!isTargetPanel && !shouldClosePanel) {
            return;
        }

        if (!isRecord(entry)) {
            if (isTargetPanel) {
                stateMap[id] = { isOpen: true, attachment: targetAttachment, activePanelId: panelId };
            }
            return;
        }

        stateMap[id] = {
            ...entry,
            ...(isTargetPanel ? { attachment: targetAttachment } : {}),
            isOpen: isTargetPanel,
            activePanelId: panelId,
        };
    });

    writePanelSessionStateMap(stateMap, storage);
};

export const closeFacilitySlideoutPanelSession = (
    panelId: FacilitySlideoutPanelId,
    storage?: PanelStateStorage | null
): void => {
    const stateMap = readPanelSessionStateMap(storage);
    const entry = stateMap[panelId];

    if (isRecord(entry)) {
        stateMap[panelId] = {
            ...entry,
            isOpen: false,
        };
    }

    if (stateMap[ACTIVE_FACILITY_SLIDEOUT_PANEL_KEY] === panelId) {
        delete stateMap[ACTIVE_FACILITY_SLIDEOUT_PANEL_KEY];
    }

    writePanelSessionStateMap(stateMap, storage);
};

export const closeFacilityAssetSetPanelSessions = (
    storage?: PanelStateStorage | null
): void => {
    closeFacilitySlideoutPanelSession(
        FACILITY_SLIDEOUT_PANEL_IDS.facilityAssetSets,
        storage
    );
    closeFacilitySlideoutPanelSession(
        FACILITY_SLIDEOUT_PANEL_IDS.assetSetVisualization,
        storage
    );
};

export const shouldRestoreFacilitySlideoutPanelOpen = (
    panelId: FacilitySlideoutPanelId,
    panelState?: PanelSessionState,
    storage?: PanelStateStorage | null
): boolean => {
    if (panelState && !panelState.isOpen) {
        return false;
    }

    const stateMap = readPanelSessionStateMap(storage);
    const activePanelId = stateMap[ACTIVE_FACILITY_SLIDEOUT_PANEL_KEY];

    if (isFacilitySlideoutPanelId(activePanelId)) {
        const entry = stateMap[panelId];
        if (isRecord(entry)) {
            return entry.isOpen === true;
        }

        return activePanelId === panelId;
    }

    const entry = stateMap[panelId];
    return isRecord(entry) ? entry.isOpen === true : panelState?.isOpen === true;
};
